import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { useFormSubmit } from '@/hooks/useFormSubmit';

const CONTACT_ADDR = 'https://readdy.ai/api/form/da1vivdv7gurs51ef1tg';

export default function ContactPage() {
  useSeo({
    title: '联系我们 - 今日同人库反馈与合作',
    description:
      '与今日同人库取得联系：提交更新勘误、汉化组投稿、合作洽谈或读者建议。我们会在收到留言后尽快处理并回复。',
    keywords: '今日同人库,联系我们,投稿合作,更新勘误,读者反馈',
  });

  const { submitting, success, error, handleSubmit, reset } = useFormSubmit(CONTACT_ADDR);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系我们',
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="bg-secondary-50/70 px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <nav className="text-sm text-foreground-500 mb-3" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">联系我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">联系我们</h1>
          <p className="mt-3 text-foreground-500 text-sm md:text-base max-w-2xl leading-relaxed">
            无论是更新勘误、汉化组投稿、合作洽谈，还是对今日同人库的建议，都可以通过下方表单告诉我们。
          </p>
        </div>
      </div>

      <div className="px-4 md:px-6 max-w-4xl mx-auto py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
            {success ? (
              <div className="text-center py-10">
                <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 text-2xl">
                  <i className="ri-check-line"></i>
                </span>
                <h2 className="mt-4 font-heading text-xl font-bold text-foreground-900">留言已收到</h2>
                <p className="mt-2 text-sm text-foreground-500">感谢你的反馈，我们会尽快处理并回复。</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-6 px-5 py-2.5 rounded-md border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  再发一条
                </button>
              </div>
            ) : (
              <form className="space-y-5" data-readdy-form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">你的称呼</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="如何称呼你"
                      className="w-full h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">联系邮箱</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">留言主题</label>
                  <select
                    id="subject"
                    name="subject"
                    className="w-full h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
                  >
                    <option>更新勘误</option>
                    <option>汉化组投稿</option>
                    <option>合作洽谈</option>
                    <option>读者建议</option>
                    <option>其它</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    placeholder="请描述你的问题或想法（500 字以内）"
                    className="w-full px-4 py-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 min-h-[140px] resize-y"
                  />
                </div>
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  placeholder=""
                />
                {error && (
                  <p className="text-sm text-accent-800 bg-accent-100 rounded-md px-4 py-3 flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {submitting ? '提交中…' : '提交留言'}
                </button>
              </form>
            )}
          </div>
        </div>

        <aside className="lg:col-span-2 space-y-5">
          <div className="bg-background-100 rounded-lg p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">联系方式</h2>
            <ul className="space-y-4 text-sm text-foreground-600">
              <li className="flex items-start gap-3">
                <i className="ri-mail-line text-primary-600 mt-0.5"></i>
                <div>
                  <p className="font-medium text-foreground-800">邮箱</p>
                  <p>contact@tongrenku.example.com</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-primary-600 mt-0.5"></i>
                <div>
                  <p className="font-medium text-foreground-800">处理时间</p>
                  <p>工作日 10:00 - 22:00 回复</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-lightbulb-flash-line text-primary-600 mt-0.5"></i>
                <div>
                  <p className="font-medium text-foreground-800">投稿合作</p>
                  <p>欢迎汉化组投稿，投稿请注明作品名称与更新频率。</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="bg-secondary-50 rounded-lg p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-3">先看这里</h2>
            <p className="text-sm text-foreground-500 leading-relaxed mb-4">
              想了解今日同人库的使用方式，可以先查阅追更指南。
            </p>
            <Link to="/faq" className="inline-flex items-center gap-1 text-secondary-700 text-sm font-medium hover:text-secondary-800 whitespace-nowrap">
              前往追更指南 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}